import Box from '@mui/material/Box';
import { ReactNode } from 'react';

import ProgressiveBackground from 'src/common/components/ProgressiveBackground';
import {
  PUBLIC_TOP_MENU_HEIGHT_PX,
  publicHeroUnderMenuOverlapPx,
} from 'src/helpers/homeHeroLayoutConstants';

interface HomeHeroSectionLayoutProps {
  backgroundImageUrl?: string;
  backgroundPosition?: string;
  children: ReactNode;
  /** When true, stays within parent width (e.g. editor preview). */
  contained?: boolean;
  /** Pull background under the sticky top menu (public home only). */
  extendUnderMenu?: boolean;
  'aria-label'?: string;
}

export default function HomeHeroSectionLayout({
  backgroundImageUrl,
  backgroundPosition = 'center center',
  children,
  contained = false,
  extendUnderMenu = false,
  'aria-label': ariaLabel,
}: HomeHeroSectionLayoutProps) {
  const hasBackground = Boolean(backgroundImageUrl?.trim());
  const underMenu = extendUnderMenu && hasBackground && !contained;
  const menuOverlapPx = underMenu ? publicHeroUnderMenuOverlapPx(0) : 0;

  return (
    <Box
      component="section"
      aria-label={ariaLabel}
      sx={theme => ({
        position: 'relative',
        boxSizing: 'border-box',
        overflow: 'visible',
        ...(contained
          ? {
              width: '100%',
              borderRadius: 1,
              minHeight: { xs: 280, md: 360 },
              maxHeight: { xs: 'none', md: 'none' },
            }
          : {
              width: '100vw',
              left: '50%',
              right: '50%',
              marginLeft: '-50vw',
              marginRight: '-50vw',
              ...(underMenu ? { mt: `-${menuOverlapPx}px` } : {}),
              [theme.breakpoints.down('md')]: {
                minHeight: 280,
              },
              [theme.breakpoints.up('md')]: {
                minHeight: '720px',
              },
            }),
      })}
    >
      {hasBackground ? (
        <ProgressiveBackground
          src={backgroundImageUrl!}
          backgroundPosition={backgroundPosition}
          top={underMenu ? `-${menuOverlapPx}px` : 0}
          height={underMenu ? `calc(100% + ${menuOverlapPx}px)` : '100%'}
        />
      ) : null}
      {hasBackground ? (
        <Box
          aria-hidden
          sx={theme => ({
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            top: underMenu ? `-${menuOverlapPx}px` : 0,
            height: underMenu ? `calc(100% + ${menuOverlapPx}px)` : '100%',
            zIndex: 1,
            pointerEvents: 'none',
            background: `linear-gradient(180deg, transparent 50%, ${theme.palette.background.default} 100%)`,
          })}
        />
      ) : null}
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          width: '100%',
          maxWidth: 'lg',
          mx: 'auto',
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'center',
          boxSizing: 'border-box',
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Box
          sx={theme => ({
            width: '100%',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            ...(underMenu
              ? {
                  pt: {
                    xs: `calc(${PUBLIC_TOP_MENU_HEIGHT_PX}px + ${theme.spacing(3)})`,
                    sm: `calc(${PUBLIC_TOP_MENU_HEIGHT_PX}px + ${theme.spacing(4)})`,
                    md: `calc(${PUBLIC_TOP_MENU_HEIGHT_PX}px + ${theme.spacing(5)})`,
                  },
                  pb: { xs: 3, sm: 4, md: 5 },
                }
              : {
                  py: { xs: 3, sm: 4, md: 5 },
                }),
          })}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
